import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Button,
  Form,
  Modal,
  notification,
  Skeleton,
  Space,
  Switch,
} from 'antd';
import { isEmpty } from 'lodash';

import { useDefaultFormErrorNotification } from '@/hooks';

import PaymentConditionForm from '../PaymentConditionForm';
import {
  useActiveOrInactivatePaymentCondition,
  usePaymentCondition,
  useUpdatePaymentCondition,
} from '@/core/domain/enterprise/PaymentCondition/PaymentCondition.hooks';
import { UpdatePaymentConditionRequest } from '@/core/domain/enterprise/PaymentCondition/PaymentCondition.types';
import Authorities from '@/types/Authority';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { hasPermissions } from '@/utils/functions/hasPermissions';

type Props = {
  visible: boolean;
  paymentId?: number;
  handleVisible: () => void;
};

const UpdatePaymentConditionModal: React.FC<Props> = ({
  visible = false,
  handleVisible,
  paymentId,
}) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();
  const [active, setActive] = useState<boolean>(false);

  const paymentCondition = usePaymentCondition(paymentId, visible);
  const paymentConditionData = paymentCondition.data;

  const activateOrInactivateMutation = useActiveOrInactivatePaymentCondition();
  const updateMutation = useUpdatePaymentCondition();

  useEffect(() => {
    if (!isEmpty(paymentConditionData) && paymentConditionData) {
      form.setFieldsValue({
        name: paymentConditionData?.name,
        percentage: paymentConditionData?.percentage,
        qtdIntallments: Object(paymentConditionData?.installments).length,
        installments: paymentConditionData?.installments,
        intervalDays: paymentConditionData.installments[0]?.numberOfDays,
      });
      setActive(paymentConditionData?.active);
    }
  }, [paymentConditionData]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      const obj: UpdatePaymentConditionRequest = {
        ...values,
        active: active,
        id: paymentConditionData?.id,
      };

      await updateMutation.mutateAsync(obj);

      handleCancel();
    } catch (err) {
      sendFormErrorNotification();
    }
  };

  const handleCancel = () => {
    handleVisible();
  };

  const handleActiveClick = async (active: boolean) => {
    try {
      activateOrInactivateMutation.mutateAsync(Number(paymentId));

      setActive(active);
    } catch (error) {
      notification.error({
        message: intl.formatMessage({
          id: 'commons.form.notification.error',
        }),
        description: intl.formatMessage({
          id: 'commons.form.notification.errorDescription',
        }),
      });
    }
  };

  return (
    <Modal
      destroyOnClose
      title={intl.formatMessage({ id: 'paymentConditions.update' })}
      open={visible}
      width={756}
      onCancel={handleCancel}
      footer={
        <SpaceForButton justifyContent="end">
          <Space align="center">
            <span>
              {intl.formatMessage({
                id: 'commons.modal.contact.form.contactList.status',
              })}
              :
            </span>

            <Switch
              unCheckedChildren={intl.formatMessage({
                id: 'commons.entity.inactive',
              })}
              checkedChildren={intl.formatMessage({
                id: 'commons.entity.active',
              })}
              loading={false}
              checked={active}
              onClick={handleActiveClick}
              disabled={
                !hasPermissions([Authorities.REGISTER_PAYMENT_CONDITIONS_EDIT])
              }
            />
          </Space>

          <Button block onClick={handleCancel}>
            {intl.formatMessage({ id: 'commons.modal.button.cancel' })}
          </Button>

          <Button
            loading={updateMutation.isLoading}
            onClick={handleOk}
            className="save-button"
            block
            style={{ display: 'inline' }}
            disabled={
              !hasPermissions([Authorities.REGISTER_PAYMENT_CONDITIONS_EDIT])
            }
          >
            {intl.formatMessage({ id: 'commons.form.button.save' })}
          </Button>
        </SpaceForButton>
      }
    >
      <Skeleton loading={paymentCondition.isLoading}>
        <PaymentConditionForm form={form} />
      </Skeleton>
    </Modal>
  );
};

export default UpdatePaymentConditionModal;
